import { useState } from 'react';
import { Info, TriangleAlert, X } from 'lucide-react';

import { useCalendarToday } from '@/lib/home-today-store';

/**
 * StatusReautorizacaoBanner — chip discreto quando o scope
 * `calendar.readonly` não está autorizado pra Service Account (R8 Bloco E)
 * OU integração Google Calendar não está configurada no ambiente.
 *
 * Pós-BUG-V2-014 / BF.2: chip inline em vez de banner Alert grande.
 * - User pode dismissar (sessão atual, sem persistência — sumirá ao recarregar
 *   se erro persistir, mas em sessão fica fora do caminho)
 * - Banner some automaticamente quando query tem sucesso (autorizado)
 * - ZERO localStorage — dismiss é state local
 *
 * Cenários cobertos:
 *   - `scopeInsufficient`: backend retornou 403 com `needsReauth=true`.
 *   - `notConfigured`: backend retornou 501 (GOOGLE_CLIENT_ID ausente).
 */

export function StatusReautorizacaoBanner() {
  const { scopeInsufficient, notConfigured } = useCalendarToday();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!scopeInsufficient && !notConfigured) return null;

  // FASE-C: emoji ⚠️/ℹ️ virou ícone Lucide; a dica perdeu o jargão de código
  // (`calendar.readonly`) — copy pt-BR simples pro usuário final.
  const config = scopeInsufficient
    ? {
        icon: <TriangleAlert className="w-3.5 h-3.5 text-warning" aria-hidden />,
        text: 'Conecte sua agenda Google',
        title:
          'Pra mostrar seus compromissos do dia, saia e entre de novo no sistema. Se não resolver, o administrador do Google Workspace precisa autorizar o acesso de leitura à agenda.',
        testId: 'reauth-banner',
      }
    : {
        icon: <Info className="w-3.5 h-3.5 text-info" aria-hidden />,
        text: 'Agenda Google não configurada',
        title:
          'A integração com o Google Agenda ainda não foi configurada neste ambiente. Seus compromissos aparecem aqui quando o administrador concluir a configuração.',
        testId: 'reauth-banner-notconfig',
      };

  return (
    <div
      data-testid={config.testId}
      className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-paper-warm dark:bg-paper-dark-warm border border-line dark:border-line-dark"
      title={config.title}
    >
      {config.icon}
      <span className="text-ink-2 dark:text-ink-dark-2 font-medium">{config.text}</span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-ink-3 dark:text-ink-dark-3 hover:text-red transition-colors"
        aria-label="Dispensar"
        data-testid="reauth-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  );
}
